import React, { useEffect, useState } from 'react';

import { Button, Collapse, IconButton, InputAdornment } from '@material-ui/core';
import { Paper, TextField, Typography } from '@material-ui/core';
import { MdKeyboardReturn } from 'react-icons/md';
import { FaRegSmile, FaRegImage } from 'react-icons/fa';
import { IoMdImages } from 'react-icons/io';
import { MdClose } from 'react-icons/md';

import { EmojiPicker, Tooltip } from 'components';
import { call, classes } from 'utils';
import ImageLibrary from './ImageLibrary';

import Media from 'routes/Dashboard/Template/components/Media';

import styles from './ChatSender.module.scss';

const ChatSender = props => {
  const [textMessage, setTextMessage] = useState('');
  const [action, setAction] = useState({});
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof action.active === 'boolean') {
      if (action.active) {
        setOpen(true);
      } else {
        const timer = setTimeout(() => {
          setOpen(false);
          setAction({});
        }, 200);

        return () => {
          clearTimeout(timer);
        };
      }
    }
  }, [action.active]);

  const sendText = () => {
    call(props.onMessage, 'text', { text: textMessage.trim() });
    setTextMessage('');
  };

  const sendImage = ({ imageFile, imageUrl, contentType }, close = true) => {
    call(props.onMessage, 'image', {
      image: imageFile,
      imageUrl,
      imageContentType: contentType,
    });

    if (close) {
      setAction(action => ({ ...action, active: false }));
    }
  };

  const renderActions = () => {
    const button = (type, Icon, title) => (
      <IconButton color="primary" onClick={() => onAction(type)}>
        <Tooltip title={title} placement="top">
          <Icon />
        </Tooltip>
      </IconButton>
    );

    const onAction = type =>
      setAction(state =>
        state.type === type ? { ...state, active: false } : { type, active: true }
      );

    return (
      <div>
        {button('emoji', FaRegSmile, 'Send emojis')}
        {button('image', FaRegImage, 'Send images/gifs')}
        {button('library', IoMdImages, 'Send images from Library')}
      </div>
    );
  };

  const renderSendImage = () => {
    const { imageFile, imageUrl, active } = action || {};

    const update = (imageFile, imageUrl, contentType) =>
      setAction(state => ({ ...state, imageFile, imageUrl, contentType }));

    return (
      <Paper className={styles.imageSender}>
        <Typography variant="subtitle2" gutterBottom align="center">
          Send Images
        </Typography>

        <div className={styles.mediaContainer}>
          <Media
            size={100}
            mediaType="image"
            mediaFile={imageFile}
            mediaUrl={imageUrl}
            onChange={update}
            onFetch={update}
          />
        </div>

        <Button
          className={styles.button}
          variant="contained"
          color="primary"
          onClick={() => sendImage(action)}
          disabled={!active || !imageUrl}
        >
          Send Image
        </Button>
      </Paper>
    );
  };

  const renderActionContent = () => {
    if (action) {
      switch (action.type) {
        case 'emoji':
          return (
            <EmojiPicker onSelect={emoji => setTextMessage(it => `${it}${emoji}`)} />
          );

        case 'image':
          return renderSendImage();

        case 'library':
          return <ImageLibrary send={value => sendImage(value, false)} />;

        default:
          break;
      }
    }

    return null;
  };

  return (
    <div className={styles.sender}>
      <TextField
        label="Send a message..."
        value={textMessage}
        onChange={({ target }) => setTextMessage(target.value)}
        onKeyDown={({ key }) => (key === 'Enter' ? sendText() : null)}
        margin="dense"
        variant="outlined"
        data-emojiable="true"
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                disabled={!textMessage || !textMessage.trim()}
                color="primary"
                edge="end"
                onClick={sendText}
              >
                <MdKeyboardReturn />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Collapse in={open} timeout={200}>
        <div
          className={classes(styles.body, action.active ? styles.visible : styles.hidden)}
        >
          <div style={{ paddingBottom: 5 }}>
            <IconButton
              color="primary"
              onClick={() => setAction(action => ({ ...action, active: false }))}
            >
              <MdClose />
            </IconButton>
          </div>

          {renderActionContent()}
        </div>
      </Collapse>

      {renderActions()}
    </div>
  );
};

export default ChatSender;
